import { http } from '../http'
import type {
  AdminStats,
  ExtensionLinks,
  ExtensionLinksPayload,
  MaintenancePayload,
  MaintenanceState,
  ScheduledJob,
  ScheduledJobDetails,
  SocialLinks,
  SocialLinksPayload,
} from '@/types/admin'

/** `GET /admin/stats` — admin or editor. Counters for the panel home. */
export function getStats() {
  return http.get<AdminStats>('/admin/stats').then((response) => response.data)
}

/**
 * `GET /admin/maintenance` — public.
 *
 * Public on purpose: the notice page must render for anonymous visitors too,
 * and this route stays reachable while maintenance is on.
 */
export function getMaintenance() {
  return http.get<MaintenanceState>('/admin/maintenance').then((response) => response.data)
}

/**
 * `PUT /admin/maintenance` — admin or editor.
 *
 * An empty `ends_at` means open-ended: the site stays down until someone turns
 * the switch off.
 */
export function setMaintenance(payload: MaintenancePayload) {
  return http.put<MaintenanceState>('/admin/maintenance', payload).then((response) => response.data)
}

/**
 * `GET /admin/social` — public.
 *
 * Public for the same reason as the maintenance state: the sidebar footer that
 * shows these icons is drawn for anonymous visitors too.
 */
export function getSocial() {
  return http.get<SocialLinks>('/admin/social').then((response) => response.data)
}

/**
 * `PUT /admin/social` — admin or editor.
 *
 * Replaces all three links: a field sent as `null` clears that icon.
 */
export function setSocial(payload: SocialLinksPayload) {
  return http.put<SocialLinks>('/admin/social', payload).then((response) => response.data)
}

/** `GET /admin/extension-links` — any logged-in user. */
export function getExtensionLinks() {
  return http.get<ExtensionLinks>('/admin/extension-links').then((response) => response.data)
}

/**
 * `PUT /admin/extension-links` — admin or editor.
 *
 * Replaces both links: a field sent as `null` removes that download card.
 */
export function setExtensionLinks(payload: ExtensionLinksPayload) {
  return http
    .put<ExtensionLinks>('/admin/extension-links', payload)
    .then((response) => response.data)
}

/** `GET /admin/jobs` — admin or editor. The scheduled jobs the panel can toggle. */
export function getJobs() {
  return http.get<ScheduledJob[]>('/admin/jobs').then((response) => response.data)
}

/**
 * `PUT /admin/jobs/{key}` — admin or editor.
 *
 * `key` is the stable identifier (e.g. "servers_update"), not the artisan
 * command name. Only gates the scheduled run, not a manual trigger elsewhere.
 */
export function setJobEnabled(key: string, enabled: boolean) {
  return http.put<ScheduledJob>(`/admin/jobs/${key}`, { enabled }).then((response) => response.data)
}

/**
 * `POST /admin/jobs/{key}/run` — admin or editor. Runs the job now, even when
 * disabled. Queued jobs are only dispatched: their real outcome shows up on a
 * later `getJobs()`.
 */
export function runJob(key: string) {
  return http.post<ScheduledJob>(`/admin/jobs/${key}/run`).then((response) => response.data)
}

/** `GET /admin/jobs/{key}/details` — admin or editor, only jobs with `has_details`. */
export function getJobDetails(key: string) {
  return http
    .get<ScheduledJobDetails>(`/admin/jobs/${key}/details`)
    .then((response) => response.data)
}
